import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PhotoIcon, VideoCameraIcon, XCircleIcon } from "@heroicons/react/24/outline";

import useContactSingle from "../../data/useContactsSingle";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { DocumentType, IDocument, documentThumbnailSrc } from "../../models/Document";
import { IContact } from "../../models/Contact";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import FileUpload from "../shared/Inputs/FileUpload";
import LoadingWheel from "../shared/LoadingWheel";

export interface ContactDocumentsModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  contactId: string;
}

export default function ContactDocumentsModal({ open, setOpen, contactId }: ContactDocumentsModalProps) {
  const { t } = useTranslation("accounts");
  const { contact, downloadDoc, uploadDoc, deleteDoc, documents } = useContactSingle(contactId, true, DocumentType.Identity);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<IDocument | null>(null);
  const { saveHandlers } = useDefaultCRUDHandlers(t("Contact Document"));
  const [idImages, setIdImages] = useState<IDocument[]>([]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [preview, setPreview] = useState("");

  const [toUpload, setToUpload] = useState([]);
  const [toDelete, setToDelete] = useState("");
  const [docType, setDocType] = useState("0");

  const [uploadIsLoading, setUploadIsLoading] = useState(false);
  const [deleteIsLoading, setDeleteIsLoading] = useState(false);

  const updateToUploadFiles = (files: any) => setToUpload({ ...files });

  // const docTypes = [...Object.values(DocumentType).filter((ct) => isNaN(Number(ct)))];
  const docTypes = [DocumentType.Identity];

  const handleOpenDelete = (docId: string) => {
    alert(docId)
    setToDelete(docId);
    setDeleteOpen(true);
  }

  useEffect(() => {
    if (contact.data) {
      setSelectedFile(null);
    }
  }, [contact.data]);

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file);
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadDoc.mutate(selectedFile, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setSelectedFile(null);
        },
      });
    }
  };

  const handleDownload = (doc: IDocument) => {
    downloadDoc.mutate({ contactId: contactId, docId: doc.id }, {
      onSuccess: (file: any) => {
        const url = window.URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = `${doc.fileName}`;
        link.click();
        URL.revokeObjectURL(url);
      }
    });
  };

  const handleDelete = () => {
    if (toDelete != "") {
      deleteDoc.mutate(toDelete, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setDeleteOpen(false);
          setToDelete("");
        },
      });
    }
  };

  const handlePreview = (doc: IDocument) => {
    var ids = {
      contactId: contactId,
      docId: doc.id
    }

    downloadDoc.mutate(ids, {
      onSuccess: (file: any) => {
        const url = window.URL.createObjectURL(file);
        setPreview(url);
        setPreviewOpen(true);
      }
    })
  }

  const handleUploadDocs = () => {
    //attach files to http request
    setUploadIsLoading(true);
    const formData = new FormData();

    for (let i = 0; i < Object.keys(toUpload).length; i++) {
      formData.append(DocumentType[DocumentType.Identity].toString(), toUpload[i]);
    }

    uploadDoc.mutate(formData, {
      onSuccess: () => {
        setUploadOpen(false);
        setToUpload([]);
      },
      onError: (error) => {
        console.log(error);
      },
      onSettled: () => {
        setUploadIsLoading(false);
      }
    })
  }

  function output(docType: IDocument[]) {
    return docType.map((doc: IDocument) => {
      const thumbnailSrc = documentThumbnailSrc(doc);

      const image = thumbnailSrc ? (
        <img src={thumbnailSrc} alt={doc.fileName!} loading="lazy" className="rounded-sm object-contain w-full h-full" />
      ) : (
        doc.contentType?.includes("video")
          ? (<VideoCameraIcon className="w-1/2 rounded-sm object-contain" />)
          : (<PhotoIcon className="w-1/2 rounded-sm object-contain" />)
      )

      return (
        <div key={doc.id} className="h-full relative flex flex-col items-center">
          <XCircleIcon className="absolute w-6 h-6 font-bold top-0 right-3 text-red-600 hover:text-red-700 hover:cursor-pointer" onClick={() => handleOpenDelete(doc.id)} />
          <div className="flex justify-center cursor-pointer" onClick={() => handlePreview(doc)}>
            {image}
          </div>
          <div className="text-center w-full">
            <div className="font-semibold">{t("Filename:")}</div>
            <p className="truncate text-sm">{doc.fileName}</p>
            <button type="button" onClick={() => handleDownload(doc)} className="font-bold text-primary-600">{t("Download")}</button>
          </div>
        </div>
      )
    })
  }

  useEffect(() => {
    if (documents.isLoading) return;
    if (!documents.data) return;

    setIdImages(documents.data?.data?.filter((doc: IDocument) => doc.type === DocumentType["Identity"])! || []);

  }, [documents.isLoading, documents.data]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={t("Delete Document?")} message={t("Are you sure you want to delete this document? \nThis is a permanent action and cannot be undone.")} confirmButtonText={t("Delete")}
        onConfirm={() => handleDelete()} isLoading={deleteIsLoading}
      />
      <Modal open={uploadOpen} setOpen={setUploadOpen} width="max-w-4xl" >
        <div>
          <h1 className="text-xl font-semibold mb-4">{t("Upload Identity Documents")}</h1>
          {uploadIsLoading ? (
            <div className="flex items-center py-[10vh]">
              <LoadingWheel />
            </div>
          ) : (
            <>
              <FileUpload
                accept=".jpg,.png,.jpeg,.pdf"
                label={t("Upload Images")}
                // desc="Make sure to select a document type" 
                multiple
                updateFilesCb={updateToUploadFiles}
              />
              <div className="flex justify-between mt-4">
                <ButtonInput classes="!bg-white border border-gray-200 !text-primary-500 hover:!bg-gray-50" label={t("Cancel")} isSubmit={false} onClick={() => setUploadOpen(false)} />
                <ButtonInput classes="" label={t("Upload")} isSubmit={false} onClick={() => handleUploadDocs()} />
              </div>
            </>
          )}
        </div>
      </Modal>
      <Modal open={open} setOpen={setOpen} width="max-w-3xl">
        <>
          <div className="flex items-center justify-between mt-2 border-t pt-4 mb-6">
            <h3 className="text-lg font-semibold">{t("Identity Images")}</h3>
            <ButtonInput
              label={t("Upload Image")}
              onClick={() => setUploadOpen(true)}
              isSubmit={false}
              classes="!bg-primary-600 !text-white hover:!bg-primary-700"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {output(idImages)}
          </div>
        </>
      </Modal>
      <Modal open={previewOpen} setOpen={setPreviewOpen} width="max-w-3xl">
        <div className="flex flex-col items-center">
          <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700" onClick={() => setPreviewOpen(false)}>
            <XCircleIcon className="w-6 h-6" />
          </button>
          {preview ? (
            <img src={preview} alt={t("Document Preview")} className="w-full h-auto object-contain" />
          ) : (
            <div className="flex items-center justify-center h-64">
              <LoadingWheel />
            </div>
          )}

        </div>
      </Modal>

    </>
  );
}